import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  SportsTennis as TennisIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { lt } from 'date-fns/locale';
import {
  addReservationWithConflictCheck,
  cancelReservation,
  getUserProfile,
  getAvailableSlotsForRange,
  getReservationsForRange,
  subscribeToAvailableSlotsForRange,
  subscribeToReservationsForRange,
  subscribeToUserReservations,
  LOCATION_INFO,
  LOCATIONS,
  getUserFutureReservations,
  getActiveCoachesList,
  filterCoachesWithUpcomingSlots,
  getCoachById,
  getTrainingPricePerHourWithPricing,
  calculateCourtPriceForReservation,
  DEFAULT_COACH_ID,
  getCoachSlug,
  getCoachIdFromSlug,
  ALL_TRAINING_VENUE_IDS,
  TRAINING_VENUES,
  getVenuesAvailableInSlot,
  getSlotVenueBackground,
  isHalfHourBlockedByReservations,
  isVenueAvailableForDuration,
  getLocationDisplayName,
  getReservationCoveringSlot,
  formatReservationCourtLabel
} from '../utils/firebaseUtils';
import VenueFilterCheckboxes from './VenueFilterCheckboxes';
import VenueDiagonalSlotCell from './VenueDiagonalSlotCell';
import ScrollableBookingCalendar from './ScrollableBookingCalendar';
import {
  getBookingCalendarDates,
  getBookingCalendarRange,
  getUnavailableSlotColor
} from '../utils/calendarDateUtils';
import { useAuth } from '../contexts/AuthContext';
import { trackPageView, trackSlotClick, trackReservationCreated } from '../utils/analyticsUtils';
import { isUserAdmin } from '../utils/adminUtils';
import { isPastDateTime, vilniusToCalendarUTC } from '../utils/dateUtils';

const BOOKING_SLIDE_MS = 320;

const VENUE_UNAVAILABLE_MESSAGE =
  'Apgailestaujame, tačiau pasirinktoje lokacijoje šiuo laiku pradėti treniruotės negalime, kviečiame pasirinkti kitą laiką ar vietą treniruotei.';

const ReservationPage = () => {
  const { coachSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get coach ID from URL slug or default
  const getCoachIdFromUrl = () => {
    if (coachSlug) {
      return getCoachIdFromSlug(coachSlug);
    }
    return DEFAULT_COACH_ID;
  };
  
  const [availableSlots, setAvailableSlots] = useState({});
  const [reservations, setReservations] = useState([]);
  const [weekReservations, setWeekReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reservationDialog, setReservationDialog] = useState(false);
  const [reservationForm, setReservationForm] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    duration: 60,
    numberOfPeople: 1,
    racketsNeeded: false,
    hasCourt: 'Ne',
    location: LOCATIONS.SEB_ARENA,
    comments: '',
    courtNo: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [conflictDialog, setConflictDialog] = useState(false);
  const [venueUnavailableDialog, setVenueUnavailableDialog] = useState(false);
  const [lastCreatedReservation, setLastCreatedReservation] = useState(null);
  const [firebaseError, setFirebaseError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedVenueIds, setSelectedVenueIds] = useState(ALL_TRAINING_VENUE_IDS);
  const [bookingStep, setBookingStep] = useState('form');
  const [venuePickPending, setVenuePickPending] = useState(null);
  const [venuePickSelected, setVenuePickSelected] = useState(null);
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [blockedSnackbarOpen, setBlockedSnackbarOpen] = useState(false);
  const cancelInProgressRef = useRef(false);
  const [courtPricePreview, setCourtPricePreview] = useState(null);
  const [courtPriceLoading, setCourtPriceLoading] = useState(false);
  const [selectedCoachId, setSelectedCoachId] = useState(getCoachIdFromUrl());
  const [activeCoaches, setActiveCoaches] = useState([]);
  const previousVisibleCoachIdsRef = useRef([]);
  const [coachPricing, setCoachPricing] = useState({});
  const [coachProfiles, setCoachProfiles] = useState({});

  const { currentUser, userProfile, canMakeReservations } = useAuth();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const selectedCoach = activeCoaches.find((c) => c.id === selectedCoachId) || getCoachById(selectedCoachId);

  // Fetch active coaches with at least one upcoming bookable slot, pricing and photos
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await getActiveCoachesList();
      if (cancelled) return;
      const withSlots = await filterCoachesWithUpcomingSlots(list, {
        previousVisibleIds: previousVisibleCoachIdsRef.current
      });
      if (cancelled) return;
      const listToUse = withSlots && withSlots.length > 0 ? withSlots : [];
      previousVisibleCoachIdsRef.current = listToUse.map((c) => c.id);
      setActiveCoaches(listToUse);
      const pricing = {};
      const profiles = {};
      listToUse.forEach((c) => {
        profiles[c.id] = { photoURL: c.photoURL, price1Person: c.price1Person, price2People: c.price2People, price3People: c.price3People, price4People: c.price4People };
        if (c.price1Person != null || c.price2People != null) {
          pricing[c.id] = {
            price1Person: c.price1Person,
            price2People: c.price2People,
            price3People: c.price3People,
            price4People: c.price4People
          };
        }
      });
      setCoachPricing(pricing);
      setCoachProfiles(profiles);
    })().catch((err) => {
      if (!cancelled) setError('Klaida kraunant trenerių sąrašą');
      console.error(err);
    });
    return () => { cancelled = true; };
  }, []);

  // Sync coach ID with URL param when slug changes
  useEffect(() => {
    if (coachSlug) {
      const coachIdFromUrl = getCoachIdFromSlug(coachSlug);
      if (coachIdFromUrl && coachIdFromUrl !== selectedCoachId) {
        setSelectedCoachId(coachIdFromUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachSlug]);

  // If selected coach has no upcoming slots (not in filtered list), switch to first available coach
  useEffect(() => {
    if (activeCoaches.length === 0) return;
    const inList = activeCoaches.some((c) => c.id === selectedCoachId);
    if (!inList) {
      const firstId = activeCoaches[0]?.id || DEFAULT_COACH_ID;
      setSelectedCoachId(firstId);
      const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';
      navigate(`/rezervacija/${getCoachSlug(firstId)}${queryString}`, { replace: true });
    }
  }, [activeCoaches, selectedCoachId, navigate, searchParams]);

  const calendarDates = useMemo(() => getBookingCalendarDates(), []);
  const bookingRange = useMemo(() => getBookingCalendarRange(), []);

  useEffect(() => {
    if (!coachSlug && location.pathname === '/rezervacija') {
      navigate(`/rezervacija/${getCoachSlug(DEFAULT_COACH_ID)}`, { replace: true });
    }
  }, [coachSlug, location.pathname, navigate]);

  // Global error handler for Firebase errors
  React.useEffect(() => {
    const handleGlobalError = (event) => {
      if (event.error && event.error.message && event.error.message.includes('FIRESTORE')) {
        console.error('Firebase error detected:', event.error);
        setFirebaseError(true);
        setError('Firebase ryšio klaida. Bandykite atnaujinti puslapį.');
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && event.reason.message && event.reason.message.includes('FIRESTORE')) {
        console.error('Firebase promise rejection:', event.reason);
        setFirebaseError(true);
        setError('Firebase ryšio klaida. Bandykite atnaujinti puslapį.');
      }
    });

    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const { startDate, endDate } = bookingRange;
      const [slots, weekRes] = await Promise.all([
        getAvailableSlotsForRange(startDate, endDate, selectedCoachId),
        getReservationsForRange(startDate, endDate, selectedCoachId)
      ]);
      
      setAvailableSlots(slots);
      setWeekReservations(weekRes);
      
      if (currentUser) {
        const userEmail = userProfile?.email || currentUser.email;
        const userReservations = userEmail ? await getUserFutureReservations(userEmail) : [];
        setReservations(userReservations);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Klaida atnaujinant duomenis');
    } finally {
      setRefreshing(false);
    }
  };

  const loadWeekData = useCallback(() => {
    setLoading(true);
    let unsubscribeSlots = null;
    let unsubscribeReservations = null;

    const { startDate, endDate } = bookingRange;
    const cId = selectedCoachId || DEFAULT_COACH_ID;

    const loadWeekDataOnce = async () => {
      try {
        const [slots, weekRes] = await Promise.all([
          getAvailableSlotsForRange(startDate, endDate, cId),
          getReservationsForRange(startDate, endDate, cId)
        ]);
        
        setAvailableSlots(slots);
        setWeekReservations(weekRes);
        setLoading(false);
      } catch (error) {
        console.error('Error loading week data:', error);
        setError('Klaida kraunant duomenis');
        setLoading(false);
      }
    };
    
    const isAdmin = currentUser && isUserAdmin(currentUser);
    const shouldUseRealTime = isAdmin || reservationDialog;
    
    if (shouldUseRealTime) {
      try {
        unsubscribeSlots = subscribeToAvailableSlotsForRange(startDate, endDate, (slots) => {
          setAvailableSlots(slots);
          setLoading(false);
        }, cId);

        unsubscribeReservations = subscribeToReservationsForRange(startDate, endDate, (reservations) => {
          setWeekReservations(reservations);
        }, cId);
      } catch (error) {
        console.error('Real-time listeners failed, falling back to one-time fetch:', error);
        loadWeekDataOnce();
      }
    } else {
      loadWeekDataOnce();
    }
    
    // Return cleanup function
    return () => {
      try {
        if (unsubscribeSlots && typeof unsubscribeSlots === 'function') {
          unsubscribeSlots();
        }
        if (unsubscribeReservations && typeof unsubscribeReservations === 'function') {
          unsubscribeReservations();
        }
      } catch (error) {
        console.error('Error cleaning up week data listeners:', error);
      }
    };
  }, [bookingRange, currentUser, reservationDialog, selectedCoachId]);

  const loadUserReservations = useCallback(() => {
    if (!currentUser) {
      setReservations([]);
      return () => {}; // Return empty cleanup function
    }
    
    let unsubscribe = null;
    
    // Use one-time fetch for cost optimization
    const loadUserReservationsOnce = async () => {
      try {
        const userEmail = userProfile?.email || currentUser.email;
        const userReservations = userEmail ? await getUserFutureReservations(userEmail) : [];
        setReservations(userReservations);
      } catch (error) {
        console.error('Error loading user reservations:', error);
        setError('Klaida kraunant rezervacijas');
      }
    };
    
    // Only use real-time listeners for admin users
    const isAdmin = currentUser && isUserAdmin(currentUser);
    
    if (isAdmin) {
      try {
        const userEmail = userProfile?.email || currentUser.email;
        unsubscribe = userEmail ? subscribeToUserReservations(userEmail, (userReservations) => {
          setReservations(userReservations);
        }) : null;
      } catch (error) {
        console.error('Real-time user reservations listener failed, falling back to one-time fetch:', error);
        loadUserReservationsOnce();
      }
    } else {
      // Use one-time fetch for regular users
      loadUserReservationsOnce();
    }
    
    return () => {
      try {
        if (unsubscribe && typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (error) {
        console.error('Error cleaning up user reservations listener:', error);
      }
    };
  }, [currentUser, userProfile?.email]);

    useEffect(() => {
    const cleanupWeekData = loadWeekData();
    const cleanupUserReservations = loadUserReservations();
    trackPageView('Reservation Page');
    
    return () => {
      try {
        if (cleanupWeekData && typeof cleanupWeekData === 'function') {
          cleanupWeekData();
        }
        if (cleanupUserReservations && typeof cleanupUserReservations === 'function') {
          cleanupUserReservations();
        }
      } catch (error) {
        console.error('Error during component cleanup:', error);
      }
    };
  }, [loadWeekData, loadUserReservations]);

  const closeBookingDialog = () => {
    setReservationDialog(false);
    setBookingStep('form');
    setVenuePickPending(null);
    setVenuePickSelected(null);
  };

  const openReservationForSlot = (date, time, venue, bookableVenues, step = 'form') => {
    setSelectedSlot({ date, time, venue, bookableVenues });
    setReservationForm((prev) => ({
      ...prev,
      clientName: userProfile?.fullName || '',
      clientPhone: userProfile?.phoneNumber || '',
      clientEmail: userProfile?.email || currentUser?.email || '',
      location: venue
    }));
    setBookingStep(step);
    setReservationDialog(true);
    if (!isUserAdmin(currentUser)) {
      handleManualRefresh();
    }
  };

  const handleVenuePickContinue = () => {
    if (!venuePickPending || !venuePickSelected) return;
    const { date, time, bookableVenues } = venuePickPending;
    setSelectedSlot({ date, time, venue: venuePickSelected, bookableVenues });
    setReservationForm((prev) => ({
      ...prev,
      clientName: userProfile?.fullName || '',
      clientPhone: userProfile?.phoneNumber || '',
      clientEmail: userProfile?.email || currentUser?.email || '',
      location: venuePickSelected
    }));
    setBookingStep('form');
    if (!isUserAdmin(currentUser)) {
      handleManualRefresh();
    }
  };

  const getDurationValidVenues = (date, time, duration = 60) =>
    (getClientSlotCellStyle(date, time).bookableVenues || []).filter((venueId) =>
      isVenueAvailableForDuration(
        date,
        time,
        duration,
        venueId,
        availableSlots,
        weekReservations,
        isSlotAvailableForBooking
      )
    );

  const handleSlotClick = (date, time, clickedVenueId = null) => {
    const cell = getClientSlotCellStyle(date, time);
    trackSlotClick(date, time, cell.type);

    if (cell.type !== 'available') {
      if (cell.type === 'unavailable' && !isSlotAvailableForBooking(date, time)) {
        const slotHour = parseInt(time.split(':')[0]);
        if (slotHour >= 7 && slotHour <= 11) {
          setError('Rezervacijos 7:00-11:00 laikui nebegalima kurti po 22:00.');
        } else {
          setError('Rezervacijos nebegalima kurti mažiau nei 3 valandos prieš treniruotę.');
        }
      }
      return;
    }

    if (!canMakeReservations()) {
      setError('Norėdami kurti rezervacijas, prašome prisijungti.');
      return;
    }

    const defaultDuration = 60;
    const durationValidVenues = getDurationValidVenues(date, time, defaultDuration);
    const clickedInvalidVenue =
      clickedVenueId &&
      cell.bookableVenues.includes(clickedVenueId) &&
      !durationValidVenues.includes(clickedVenueId);

    if (clickedInvalidVenue) {
      if (isMobileViewport && durationValidVenues.length > 0) {
        setVenuePickPending({ date, time, bookableVenues: durationValidVenues });
        setVenuePickSelected(durationValidVenues[0]);
        openReservationForSlot(date, time, durationValidVenues[0], durationValidVenues, 'venue');
        return;
      }
      setVenueUnavailableDialog(true);
      return;
    }

    if (durationValidVenues.length === 0) {
      if (clickedVenueId && cell.bookableVenues.includes(clickedVenueId)) {
        setVenueUnavailableDialog(true);
      } else {
        setError('Pasirinktas laikas nepasiekiamas. Prašome pasirinkti kitą laiką.');
      }
      return;
    }

    if (durationValidVenues.length > 1) {
      const preselected =
        clickedVenueId && durationValidVenues.includes(clickedVenueId)
          ? clickedVenueId
          : durationValidVenues[0];

      if (isMobileViewport) {
        setVenuePickPending({ date, time, bookableVenues: durationValidVenues });
        setVenuePickSelected(preselected);
        openReservationForSlot(date, time, preselected, durationValidVenues, 'venue');
        return;
      }

      openReservationForSlot(date, time, preselected, durationValidVenues);
      return;
    }

    openReservationForSlot(date, time, durationValidVenues[0], durationValidVenues);
  };

  const handleReservationSubmit = async () => {
    if (!selectedSlot) return;

    setSubmitting(true);
    setError('');

    // Check if user is blocked
    if (currentUser && !currentUser.isAnonymous) {
      try {
        const profile = await getUserProfile(currentUser.uid);
        if (profile?.blocked) {
          setBlockedSnackbarOpen(true);
          setSubmitting(false);
          return;
        }
      } catch {
        // If we can't fetch profile, allow reservation attempt
      }
    }

    // Validate court number is required when hasCourt is 'Taip'
    if (reservationForm.hasCourt === 'Taip' && !reservationForm.courtNo?.trim()) {
      setError('Prašome įvesti aikštelės numerį.');
      setSubmitting(false);
      return;
    }

    // Final validation: check slot is available at selected venue for duration
    if (!areAllSlotsAvailableForDuration(selectedSlot.date, selectedSlot.time, reservationForm.duration, reservationForm.location)) {
      setError('Pasirinktas laikas nepasiekiamas. Prašome pasirinkti kitą laiką.');
      setSubmitting(false);
      return;
    }
    const bookableAtVenue = getClientSlotCellStyle(selectedSlot.date, selectedSlot.time).bookableVenues;
    if (!bookableAtVenue.includes(reservationForm.location)) {
      setError('Pasirinktas laikas neatitinka vietos. Prašome pasirinkti laiką iš sąrašo.');
      setSubmitting(false);
      return;
    }

    try {
      const reservationData = {
        ...reservationForm,
        date: selectedSlot.date,
        startTime: selectedSlot.time,
        courtNo: reservationForm.hasCourt === 'Taip' ? (reservationForm.courtNo || '').trim() : '',
        coachId: selectedCoachId,
        coachName: selectedCoach?.name || ''
      };

      await addReservationWithConflictCheck(reservationData);
      
      // Track successful reservation
      trackReservationCreated({
        ...reservationData,
        totalPrice: calculateTotalPrice()
      });
      
      // Store the created reservation data for calendar use
      setLastCreatedReservation({
        ...reservationData,
        totalPrice: calculateTotalPrice()
      });
      
      // Real-time listeners will automatically update the UI
      
      // Set success message based on court availability
      const message = reservationForm.hasCourt === 'Taip' 
        ? 'Sutarta, susitiksime aikštelėje.'
        : 'Aikštelės numerį informuosime vėliau. Iki susitikimo!';
      
      setSuccessMessage(message);
      setSuccessDialog(true);
      
      // Reset form and close dialog
      setReservationForm({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        duration: 60,
        numberOfPeople: 1,
        racketsNeeded: false,
        hasCourt: 'Ne',
        comments: '',
        courtNo: '',
        location: LOCATIONS.SEB_ARENA
      });
      closeBookingDialog();
      setSelectedSlot(null);
    } catch (error) {
      console.error('Error creating reservation:', error);
      
      if (error.message === 'TIME_CONFLICT') {
        setConflictDialog(true);
      } else {
        setError('Klaida kuriant rezervaciją');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isSlotAvailableForBooking = (date, time) => {
    const now = new Date();
    
    // Block past dates entirely
    if (isPastDateTime(date, time)) {
      return false;
    }
    
    const slotHour = parseInt(time.split(':')[0]);
    
    // For times 7:00-11:00: use specific hour-based cutoff times
    if (slotHour >= 7 && slotHour <= 11) {
      const slotDateTime = new Date(`${date}T${time}`);
      
      // Calculate cutoff hours based on time slot
      let cutoffHours = 0;
      if (time === '07:00') cutoffHours = 9;
      else if (time === '07:30') cutoffHours = 9.5;
      else if (time === '08:00') cutoffHours = 10;
      else if (time === '08:30') cutoffHours = 10.5;
      else if (time === '09:00') cutoffHours = 11;
      else if (time === '09:30') cutoffHours = 11.5;
      else if (time === '10:00') cutoffHours = 12;
      else if (time === '10:30') cutoffHours = 12.5;
      else cutoffHours = 3; // fallback for any other 7:00-11:00 times
      
      const cutoffTime = new Date(slotDateTime.getTime() - cutoffHours * 60 * 60 * 1000);
      const isAvailable = now < cutoffTime;
      return isAvailable;
    } else {
      // For all other times: use 3-hour rule
      const slotDateTime = new Date(`${date}T${time}`);
      const cutoffTime = new Date(slotDateTime.getTime() - 3 * 60 * 60 * 1000); // 3 hours before
      const isAvailable = now < cutoffTime;
      return isAvailable;
    }
  };

  // Check if all time slots within a duration are available for booking
  const areAllSlotsAvailableForDuration = (date, startTime, duration, venueId) =>
    isVenueAvailableForDuration(
      date,
      startTime,
      duration,
      venueId,
      availableSlots,
      weekReservations,
      isSlotAvailableForBooking
    );

  const getClientSlotCellStyle = (date, time) => {
    const unavailableColor = getUnavailableSlotColor(date);

    if (!isSlotAvailableForBooking(date, time)) {
      return { type: 'unavailable', background: unavailableColor, backgroundColor: unavailableColor, bookableVenues: [] };
    }

    const userReservation = reservations.find(
      (reservation) =>
        reservation.date === date &&
        reservation.status !== 'cancelled' &&
        getReservationCoveringSlot(date, time, [reservation])
    );
    if (userReservation) {
      const venueColor =
        TRAINING_VENUES.find((v) => v.id === userReservation.location)?.color || '#d32f2f';
      return { type: 'user_reservation', background: venueColor, bookableVenues: [] };
    }

    const slot = availableSlots[date]?.timeSlots?.find((s) => s.time === time);
    const displayVenues = (getVenuesAvailableInSlot(slot, selectedVenueIds) || []).filter(
      (venueId) => !isHalfHourBlockedByReservations(date, time, venueId, weekReservations)
    );

    if (displayVenues.length === 0) {
      return { type: 'unavailable', background: unavailableColor, backgroundColor: unavailableColor, bookableVenues: [] };
    }

    const bg = getSlotVenueBackground({ venues: Object.fromEntries(displayVenues.map((id) => [id, true])) }, displayVenues);
    const isGradient = typeof bg === 'string' && bg.includes('gradient');
    return {
      type: 'available',
      background: bg,
      backgroundColor: isGradient ? undefined : bg,
      bookableVenues: displayVenues
    };
  };


  const getPricingForCoach = (coachId) => coachPricing[coachId] || null;

  // Fetch court price for preview when booking without court (prefer form location; fallback to slot venue for first paint)
  const courtPriceLocation = reservationForm.location || selectedSlot?.venue;
  useEffect(() => {
    if (!reservationDialog || !selectedSlot || reservationForm.hasCourt !== 'Ne' || !courtPriceLocation) {
      setCourtPricePreview(null);
      setCourtPriceLoading(false);
      return;
    }
    let cancelled = false;
    setCourtPriceLoading(true);
    setCourtPricePreview(null);
    calculateCourtPriceForReservation(
      courtPriceLocation,
      selectedSlot.date,
      selectedSlot.time,
      reservationForm.duration
    )
      .then((courtPrice) => {
        if (!cancelled) {
          setCourtPricePreview(courtPrice);
        }
      })
      .catch((err) => {
        console.warn('Court price fetch failed:', err);
        if (!cancelled) setCourtPricePreview(0);
      })
      .finally(() => {
        if (!cancelled) setCourtPriceLoading(false);
      });
    return () => { cancelled = true; };
  }, [reservationDialog, selectedSlot, reservationForm.hasCourt, courtPriceLocation, reservationForm.duration]);

  const calculateTotalPrice = () => {
    if (!selectedSlot) return 0;
    const pricing = getPricingForCoach(selectedCoachId);
    const basePrice = getTrainingPricePerHourWithPricing(reservationForm.numberOfPeople, pricing);
    const durationInHours = reservationForm.duration / 60;
    const trainingTotal = basePrice * durationInHours;
    if (reservationForm.hasCourt === 'Ne' && courtPricePreview != null) {
      return (trainingTotal + courtPricePreview).toFixed(2);
    }
    return trainingTotal.toFixed(2);
  };

  const calculateReservationTrainingPrice = (reservation) => {
    if (reservation.totalPrice != null) return Number(reservation.totalPrice).toFixed(2);
    const coachId = reservation.coachId || selectedCoachId;
    const pricing = getPricingForCoach(coachId);
    const basePrice = getTrainingPricePerHourWithPricing(reservation.numberOfPeople, pricing);
    const durationInHours = reservation.duration / 60;
    const trainingPrice = basePrice * durationInHours;
    return trainingPrice.toFixed(2);
  };

  // Check if reservation can be cancelled (more than 60 hours before)
  const canCancelReservation = (reservation) => {
    const reservationDateTime = new Date(`${reservation.date}T${reservation.startTime}`);
    const now = new Date();
    const hoursDifference = (reservationDateTime - now) / (1000 * 60 * 60);
    return hoursDifference > 60;
  };

  // Add reservation to local calendar (times in Europe/Vilnius)
  const addToCalendar = (reservation) => {
    const locationName = getLocationDisplayName(reservation.location);
    
    const coachName = reservation.coachName || 'Elviu';
    const eventTitle = `Tenisas su ${coachName} ${locationName}`;
    const hasCourt = reservation.hasCourt === 'Taip';
    const courtNo = (reservation.courtNo || reservation.sebCourtName || '').trim();
    const courtLine = hasCourt ? (courtNo ? `Aikštelė: Taip, Nr. ${courtNo}` : 'Aikštelė: Taip') : 'Aikštelė: Ne';
    const eventDescription = `${courtLine}\nVieta: ${locationName}\nTelefonas: +37069614152\nEl. Paštas: gotovskiselvis@gmail.com`;
    const datesParam = vilniusToCalendarUTC(reservation.date, reservation.startTime, reservation.duration);
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&details=${encodeURIComponent(eventDescription)}&dates=${datesParam}`;
    
    window.open(calendarUrl, '_blank');
  };

  // Cancel reservation (no page reload – stay on current coach view)
  const handleCancelReservation = async (reservationId) => {
    await cancelReservation(reservationId);
    setError('');
    // Refresh user's reservations list so cancelled one disappears; keep selected coach
    const userEmail = userProfile?.email || currentUser?.email;
    if (userEmail) {
      const updated = await getUserFutureReservations(userEmail);
      setReservations(updated);
    }
  };

  const handleCancelClick = (reservation) => {
    if (cancelInProgressRef.current) return;
    setReservationToCancel(reservation);
    setCancelConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (!reservationToCancel || cancelInProgressRef.current) return;
    cancelInProgressRef.current = true;
    setCancelling(true);
    const id = reservationToCancel.id;
    try {
      await handleCancelReservation(id);
      setCancelConfirmDialog(false);
      setReservationToCancel(null);
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      setError('Klaida atšaukiant rezervaciją');
      // Keep dialog open so user can click Ne
    } finally {
      setCancelling(false);
      cancelInProgressRef.current = false;
    }
  };

  // Phone number validation - only allow digits and + sign
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits, + sign, and spaces
    const filteredValue = value.replace(/[^\d+\s-]/g, '');
    setReservationForm(prev => ({
      ...prev,
      clientPhone: filteredValue
    }));
  };

  const timeSlots = [
    '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
    '19:00', '19:30', '20:00', '20:30', '21:00', '21:30'
  ];

  if (firebaseError) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <Container maxWidth="sm">
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Typography variant="h5" component="h1" sx={{ mb: 2, fontWeight: 700, color: 'error.main' }}>
              Ryšio klaida
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Nepavyko prisijungti prie duomenų. Bandykite atnaujinti puslapį.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                setFirebaseError(false);
                setError('');
                setLoading(true);
                window.location.reload();
              }}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1.5 }}
            >
              Atnaujinti puslapį
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.50'
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 1, sm: 2, md: 5 }
        }}
      >
        {(!currentUser || currentUser.isAnonymous) && (
          <Alert severity="info" sx={{ mb: { xs: 1.5, md: 3 }, borderRadius: 2, flexShrink: 0 }}>
            <Typography variant="body2">
              Galite peržiūrėti laisvus laikus, bet norėdami kurti rezervacijas, prašome{' '}
              <Link to="/login" style={{ fontWeight: 600, textDecoration: 'underline' }}>
                prisijungti
              </Link>.
            </Typography>
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: { xs: 1.5, md: 3 },
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              flexShrink: 0
            }}
          >
            {error}
          </Alert>
        )}

        {/* Coach selector - top */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, md: 3 }, mb: { xs: 1.5, md: 3 }, flexWrap: 'wrap', flexShrink: 0 }}>
          {activeCoaches.map((coach) => {
            const isSelected = selectedCoachId === coach.id;
            const initials = (coach.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2);
            return (
              <Box
                key={coach.id}
                onClick={() => {
                  setSelectedCoachId(coach.id);
                  navigate(`/rezervacija/${getCoachSlug(coach.id)}`);
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  opacity: isSelected ? 1 : 0.75,
                  transition: 'opacity 0.2s, transform 0.2s',
                  '&:hover': { opacity: 1 },
                  transform: isSelected ? 'scale(1.05)' : 'scale(1)'
                }}
              >
                <Avatar
                  src={(coachProfiles[coach.id]?.photoURL ?? coach.photoURL) || undefined}
                  sx={{
                    width: 64,
                    height: 64,
                    border: isSelected ? 4 : 2,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    boxShadow: isSelected ? '0 0 0 3px rgba(72, 187, 120, 0.35)' : 'none',
                    bgcolor: (coachProfiles[coach.id]?.photoURL ?? coach.photoURL) ? undefined : 'primary.light',
                    color: 'primary.contrastText',
                    fontWeight: 700,
                    fontSize: '1.25rem'
                  }}
                >
                  {(coachProfiles[coach.id]?.photoURL ?? coach.photoURL) ? null : initials}
                </Avatar>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: isSelected ? 700 : 500, textAlign: 'center', maxWidth: 100 }}>
                  {coach.name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: 1, md: 2 },
            px: { xs: 1, sm: 0 },
            flexShrink: 0
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.6rem' } }}
          >
            Rezervacijos
          </Typography>
          {!isUserAdmin(currentUser) && (
            <Button
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleManualRefresh}
              disabled={refreshing}
              variant="text"
              size="small"
              title="Atnaujinti duomenis"
              sx={{ fontSize: '0.875rem', textTransform: 'none', borderRadius: 2 }}
            >
              Atnaujinti
            </Button>
          )}
        </Box>

        <Box sx={{ flexShrink: 0, mb: { xs: 1, md: 1.5 } }}>
          <VenueFilterCheckboxes
            selectedVenueIds={selectedVenueIds}
            onChange={setSelectedVenueIds}
            label="Rodyti vietas"
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 280, md: 320 },
            display: 'flex',
            flexDirection: 'column',
            mb: { xs: 2, md: 3 }
          }}
        >
          <ScrollableBookingCalendar
            calendarDates={calendarDates}
            timeSlots={timeSlots}
            fillContainer
            renderSlotCell={(date, time, index) => {
            const cell = getClientSlotCellStyle(date, time);
            const isHourEnd = (index + 1) % 2 === 0;
            const isClickable = cell.type === 'available';
            return (
              <VenueDiagonalSlotCell
                key={`${date}-${time}`}
                cell={cell}
                isClickable={isClickable}
                isHourEnd={isHourEnd}
                isLastSlot={index === timeSlots.length - 1}
                onClick={(_event, clickedVenueId) => handleSlotClick(date, time, clickedVenueId)}
              />
            );
            }}
          />
        </Box>

        {/* Your reservations - card grid */}
        {reservations.filter(r => r.status !== 'cancelled').length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 2, textAlign: 'center', fontWeight: 700, letterSpacing: '-0.01em' }}>
              Jūsų rezervacijos
            </Typography>
            <Grid container spacing={2}>
              {reservations.filter(r => r.status !== 'cancelled').map((reservation) => (
                <Grid xs={12} md={6} lg={4} key={reservation.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      height: '100%',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: reservation.status === 'cancelled' ? 'error.main' : 'divider',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      transition: 'box-shadow 0.2s ease',
                      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                        Tenisas su {reservation.coachName || 'Elviu'} {getLocationDisplayName(reservation.location)}
                      </Typography>
                      <Chip
                        label={reservation.status === 'confirmed' ? 'Patvirtinta' : 'Laukia'}
                        color={reservation.status === 'confirmed' ? 'success' : 'warning'}
                        size="small"
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gap: 0.75, mb: 2 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>Data:</Box>
                        {format(new Date(reservation.date), 'yyyy-MM-dd', { locale: lt })}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>Laikas:</Box>
                        {reservation.startTime} – {reservation.endTime}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>Aikštelė:</Box>
                        {formatReservationCourtLabel(reservation)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>Kaina:</Box>
                        {calculateReservationTrainingPrice(reservation)} €{reservation.hasCourt !== 'Taip' && reservation.totalPrice != null ? ' (pilna kaina)' : reservation.hasCourt === 'Taip' ? ' (treniruotė)' : ' (treniruotė + aikštelė)'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EventIcon />}
                        onClick={() => addToCalendar(reservation)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1 }}
                      >
                        Į kalendorių
                      </Button>
                      {canCancelReservation(reservation) && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancelClick(reservation)}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1 }}
                        >
                          Atšaukti
                        </Button>
                      )}
                    </Box>
                    {!canCancelReservation(reservation) && (
                      <Typography variant="caption" color="error" sx={{ mt: 1.5, display: 'block' }}>
                        Atšaukti negalima. Skambinkite:{' '}
                        <Typography component="a" href="tel:+37069614152" sx={{ color: 'error.main', textDecoration: 'underline', fontWeight: 600 }}>
                          +37069614152
                        </Typography>
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Booking dialog: venue pick → reservation form (single dialog, slide transition) */}
        <Dialog
          open={reservationDialog}
          onClose={closeBookingDialog}
          maxWidth={bookingStep === 'form' ? 'md' : 'xs'}
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: { xs: '92dvh', sm: '90vh' },
              transition: (theme) =>
                theme.transitions.create('max-width', {
                  duration: BOOKING_SLIDE_MS,
                  easing: theme.transitions.easing.easeInOut
                })
            }
          }}
        >
          <DialogTitle
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              fontSize: bookingStep === 'form' ? '1.25rem' : '1.1rem',
              pb: 1
            }}
          >
            {bookingStep === 'venue' ? (
              'Pasirinkite vietą'
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarIcon color="primary" />
                Nauja rezervacija
              </Box>
            )}
          </DialogTitle>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                width: '200%',
                flex: 1,
                minHeight: 0,
                alignItems: 'stretch',
                transform: bookingStep === 'venue' ? 'translateX(0)' : 'translateX(-50%)',
                transition: (theme) =>
                  theme.transitions.create('transform', {
                    duration: BOOKING_SLIDE_MS,
                    easing: theme.transitions.easing.easeInOut
                  })
              }}
            >
              {/* Step 1 — venue */}
              <Box
                sx={{
                  width: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  px: 3,
                  pb: 3,
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {venuePickPending && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {format(new Date(`${venuePickPending.date}T12:00:00`), 'EEEE, d MMM', { locale: lt })} · {venuePickPending.time}
                  </Typography>
                )}
                {(venuePickPending?.bookableVenues || []).map((venueId) => {
                  const venue = TRAINING_VENUES.find((v) => v.id === venueId);
                  const selected = venuePickSelected === venueId;
                  return (
                    <Box
                      key={venueId}
                      onClick={() => setVenuePickSelected(venueId)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: selected ? (venue?.color || 'primary.main') : 'divider',
                        bgcolor: selected ? 'action.hover' : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: venue?.color || 'grey.500' }} />
                      <Typography sx={{ fontWeight: selected ? 700 : 500, flex: 1 }}>
                        {getLocationDisplayName(venueId)}
                      </Typography>
                    </Box>
                  );
                })}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    onClick={closeBookingDialog}
                    variant="outlined"
                    sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Grįžti
                  </Button>
                  <Button
                    onClick={handleVenuePickContinue}
                    variant="contained"
                    disabled={!venuePickSelected}
                    sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    Tęsti
                  </Button>
                </Box>
              </Box>

              {/* Step 2 — reservation form */}
              <Box
                sx={{
                  width: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden'
                }}
              >
          <DialogContent
            sx={{
              pt: 0,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {/* Selected date and time (fixed — change via calendar) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Pasirinktas laikas</Typography>
              <Grid container spacing={2}>
                <Grid xs={12} sm={8} md={7}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Data"
                    value={selectedSlot ? format(new Date(selectedSlot.date + 'T12:00:00'), 'EEEE, yyyy-MM-dd', { locale: lt }) : ''}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'grey.50' },
                      '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '0.9375rem' } }
                    }}
                  />
                </Grid>
                <Grid xs={12} sm={4} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Laikas"
                    value={selectedSlot?.time || ''}
                    InputProps={{ readOnly: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'grey.50' } }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Contact info */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>Kontaktinė informacija</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Vardas ir pavardė"
                  value={reservationForm.clientName}
                  onChange={(e) => setReservationForm({ ...reservationForm, clientName: e.target.value })}
                  required
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Telefonas"
                  value={reservationForm.clientPhone}
                  onChange={handlePhoneChange}
                  required
                  size="small"
                  inputProps={{ pattern: '[0-9+\\s\\-]*', title: 'Tik skaičiai, + ženklas' }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="El. paštas"
                  type="email"
                  value={reservationForm.clientEmail}
                  onChange={(e) => setReservationForm({ ...reservationForm, clientEmail: e.target.value })}
                  required
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            {/* Duration & location (venue hidden for Seb-only coaches) */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>Trukmė ir vieta</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Trukmė"
                  value={reservationForm.duration}
                  size="small"
                  onChange={(e) => {
                    const newDuration = Number(e.target.value);
                    if (
                      selectedSlot &&
                      reservationForm.location &&
                      !isVenueAvailableForDuration(
                        selectedSlot.date,
                        selectedSlot.time,
                        newDuration,
                        reservationForm.location,
                        availableSlots,
                        weekReservations,
                        isSlotAvailableForBooking
                      )
                    ) {
                      setError('Pasirinktu laiku ši trukmė negalima. Pasirinkite kitą laiką kalendoriuje.');
                      return;
                    }
                    setReservationForm({ ...reservationForm, duration: newDuration });
                    setError('');
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value={60}>1 valanda</MenuItem>
                  <MenuItem value={90}>1,5 valandos</MenuItem>
                </TextField>
              </Grid>
              <Grid xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Vieta"
                  value={
                    reservationForm.location
                      ? `${LOCATION_INFO[reservationForm.location]?.name || getLocationDisplayName(reservationForm.location)}${LOCATION_INFO[reservationForm.location]?.address ? ` – ${LOCATION_INFO[reservationForm.location].address}` : ''}`
                      : ''
                  }
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'grey.50' } }}
                />
              </Grid>
            </Grid>

            {/* People count & court */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>Žmonių skaičius ir aikštelė</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="res-people-label">Žmonių skaičius</InputLabel>
                  <Select
                    labelId="res-people-label"
                    label="Žmonių skaičius"
                    value={reservationForm.numberOfPeople}
                    onChange={(e) => setReservationForm({ ...reservationForm, numberOfPeople: Number(e.target.value) })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value={1}>1 asmuo</MenuItem>
                    <MenuItem value={2}>2 asmenys</MenuItem>
                    <MenuItem value={3}>3 asmenys</MenuItem>
                    <MenuItem value={4}>4 asmenys</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={12}>
                <FormControl fullWidth size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="res-court-label">Ar turite aikštelę?</InputLabel>
                  <Select
                    labelId="res-court-label"
                    label="Ar turite aikštelę?"
                    value={reservationForm.hasCourt}
                    onChange={(e) => setReservationForm({ ...reservationForm, hasCourt: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="Taip">Taip</MenuItem>
                    <MenuItem value="Ne">Ne</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {reservationForm.hasCourt === 'Taip' && (
                <Grid xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Aikštelės numeris"
                    value={reservationForm.courtNo}
                    onChange={(e) => setReservationForm({ ...reservationForm, courtNo: e.target.value })}
                    placeholder="pvz Hard 3, K2..."
                    inputProps={{ maxLength: 20 }}
                    required
                    helperText={reservationForm.hasCourt === 'Taip' && !reservationForm.courtNo?.trim() ? 'Nurodykite aikštelės numerį' : ''}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& input': {
                          color: reservationForm.hasCourt === 'Taip' && !reservationForm.courtNo?.trim() ? 'grey.600' : 'text.primary'
                        }
                      },
                      '& .MuiFormHelperText-root': {
                        color: reservationForm.hasCourt === 'Taip' && !reservationForm.courtNo?.trim() ? 'text.primary' : 'text.secondary'
                      }
                    }}
                  />
                </Grid>
              )}
            </Grid>

            {/* Comments (always visible) */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>Komentarai</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Komentarai..."
              value={reservationForm.comments}
              onChange={(e) => setReservationForm({ ...reservationForm, comments: e.target.value })}
              size="small"
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {/* Rackets */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={reservationForm.racketsNeeded}
                  onChange={(e) => setReservationForm({ ...reservationForm, racketsNeeded: e.target.checked })}
                />
              }
              label="Reikia rakečių"
              sx={{ mb: 2, display: 'block' }}
            />

            {/* Price */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {(() => {
                  const pricing = getPricingForCoach(selectedCoachId);
                  const trainingOnly = selectedSlot
                    ? (getTrainingPricePerHourWithPricing(reservationForm.numberOfPeople, pricing) * (reservationForm.duration / 60))
                    : 0;
                  const courtPart = reservationForm.hasCourt === 'Ne' && courtPricePreview != null ? courtPricePreview : 0;
                  const total = reservationForm.hasCourt === 'Taip' ? trainingOnly : trainingOnly + courtPart;
                  if (reservationForm.hasCourt === 'Taip') {
                    return `Kaina: ${Number(total).toFixed(2)} € (${Number(trainingOnly).toFixed(2)} € (treniruotės kaina))`;
                  }
                  if (courtPriceLoading) return 'Skaičiuojama pilna kaina...';
                  if (courtPricePreview != null) {
                    return `Kaina: ${Number(total).toFixed(2)} € (${Number(trainingOnly).toFixed(2)} € (treniruotės kaina) + ${Number(courtPart).toFixed(2)} € (Aikštelės kaina))`;
                  }
                  return `Kaina: ${Number(trainingOnly).toFixed(2)} € (treniruotė) + aikštelė pagal kainoraštį`;
                })()}
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ flexShrink: 0, px: 3, pb: 2, pt: 0, gap: 1 }}>
            <Button onClick={closeBookingDialog} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Atšaukti
            </Button>
            <Button
              onClick={handleReservationSubmit}
              variant="contained"
              disabled={submitting || !reservationForm.clientName || !reservationForm.clientPhone || !reservationForm.clientEmail || (reservationForm.hasCourt === 'Taip' && !reservationForm.courtNo?.trim())}
              startIcon={submitting ? <CircularProgress size={20} /> : <TennisIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
            >
              {submitting ? 'Kuriama...' : 'Rezervuoti'}
            </Button>
          </DialogActions>
              </Box>
            </Box>
          </Box>
        </Dialog>

        {/* Success Dialog - modern */}
        <Dialog
          open={successDialog}
          onClose={() => setSuccessDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: reservationForm.hasCourt === 'Ne' ? 'warning.main' : 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Rezervacija sukurta!</Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>{successMessage}</Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1.5 }}>
            <Button
              onClick={() => {
                if (!lastCreatedReservation) return;
                const r = lastCreatedReservation;
                const locationName = getLocationDisplayName(r.location);
                const coachName = r.coachName || 'Elviu';
                const eventTitle = `Tenisas su ${coachName} ${locationName}`;
                const hasCourt = r.hasCourt === 'Taip';
                const courtNo = (r.courtNo || r.sebCourtName || '').trim();
                const courtLine = hasCourt ? (courtNo ? `Aikštelė: Taip, Nr. ${courtNo}` : 'Aikštelė: Taip') : 'Aikštelė: Ne';
                const eventDescription = `${courtLine}\nVieta: ${locationName}\nTelefonas: +37069614152\nEl. Paštas: gotovskiselvis@gmail.com`;
                const datesParam = vilniusToCalendarUTC(r.date, r.startTime, r.duration);
                const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&details=${encodeURIComponent(eventDescription)}&dates=${datesParam}`;
                window.open(calendarUrl, '_blank');
              }}
              variant="outlined"
              startIcon={<EventIcon />}
              size="large"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Pridėti į kalendorių
            </Button>
            <Button
              onClick={() => { setSuccessDialog(false); setLastCreatedReservation(null); }}
              variant="contained"
              color={lastCreatedReservation?.hasCourt === 'Ne' ? 'warning' : 'success'}
              size="large"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
            >
              Gerai
            </Button>
          </DialogActions>
        </Dialog>

        {/* Venue unavailable at selected wedge / duration */}
        <Dialog
          open={venueUnavailableDialog}
          onClose={() => setVenueUnavailableDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CancelIcon sx={{ fontSize: 56, color: 'warning.main' }} />
              <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                {VENUE_UNAVAILABLE_MESSAGE}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              onClick={() => setVenueUnavailableDialog(false)}
              variant="contained"
              size="large"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
            >
              Gerai
            </Button>
          </DialogActions>
        </Dialog>

        {/* Conflict Dialog - modern */}
        <Dialog
          open={conflictDialog}
          onClose={() => setConflictDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CancelIcon sx={{ fontSize: 56, color: 'error.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>Šis laikas jau užimtas</Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>Prašome pasirinkti kitą laiką.</Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button onClick={() => setConflictDialog(false)} variant="contained" color="error" size="large" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}>
              Suprantu
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel confirmation Dialog - modern */}
        <Dialog
          open={cancelConfirmDialog}
          onClose={cancelling ? undefined : () => setCancelConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CancelIcon sx={{ fontSize: 56, color: 'error.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Atšaukti rezervaciją?</Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1.5 }}>
            <Button onClick={() => !cancelling && setCancelConfirmDialog(false)} variant="outlined" size="large" disabled={cancelling} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Ne
            </Button>
            <Button onClick={handleConfirmCancel} variant="contained" color="error" size="large" disabled={cancelling} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }} startIcon={cancelling ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : null}>
              {cancelling ? 'Atšaukiama...' : 'Taip, atšaukti'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Blocked user Snackbar */}
        <Snackbar
          open={blockedSnackbarOpen}
          autoHideDuration={8000}
          onClose={() => setBlockedSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: 2 }}
        >
          <Alert
            severity="error"
            onClose={() => setBlockedSnackbarOpen(false)}
            sx={{
              width: '100%',
              maxWidth: 480,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              '& .MuiAlert-message': { py: 0.5 }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Apgailestaujame, tačiau prieiga rezervuoti paslaugas jums apribota. Norint tai pakeisti susisiekite el. paštu{' '}
              <Typography component="a" href="mailto:info@tenisouzsiemimai.lt" sx={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}>
                info@tenisouzsiemimai.lt
              </Typography>
            </Typography>
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ReservationPage; 